import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AlertService } from './alert.service';

@Injectable({
	providedIn: 'root'
})

// only for oup
export class OUPCookiesService {
	public isGtmLoaded = false;
	public isOneTrustLoaded = false;
	private consentSubject = new BehaviorSubject<boolean>(false);
	private consentListener: () => void;
	
	
	constructor(private ngZone: NgZone, public translate:TranslateService, private als: AlertService) {
		this.consentListener = this.checkConsent.bind(this);
	}

	destroy() {
		window.removeEventListener('OnetrustConsentChanged', this.consentListener);
	}
	
	// Load GTM script dynamically
	private loadGtm(gtmId: string): void {
		if (this.isGtmLoaded) return;
		
		const script = document.createElement('script');
		script.async = true;
		script.type = 'text/javascript';
		script.setAttribute('data-ot-ignore', '');
		script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}&l=dataLayer`;
		const firstScript = document.getElementsByTagName('script')[0];
		firstScript.parentNode.insertBefore(script, firstScript);
		
		// Initialize dataLayer
		window['dataLayer'] = window['dataLayer'] || [];
		window['dataLayer'].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
		
		// Add noscript iframe
		const noscriptHtml = `		
			<noscript>
				<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>
			</noscript>`;

		// Insert right after <body> opens (best practice)
		document.body.insertAdjacentHTML('afterbegin', noscriptHtml);
		this.isGtmLoaded = true;
	}
	
	// Load OneTrust script dynamically
	private loadOneTrust(oneTrustId: string): void {
		if (this.isOneTrustLoaded) return;
		
		// Load OtAutoBlock.js
		const autoBlockScript = document.createElement('script');
		autoBlockScript.type = 'text/javascript';
		autoBlockScript.src = `https://cdn-ukwest.onetrust.com/consent/${oneTrustId}/OtAutoBlock.js`;
		document.head.appendChild(autoBlockScript);
		
		// Load otSDKStub.js
		const sdkStubScript = document.createElement('script');
		sdkStubScript.type = 'text/javascript';
		sdkStubScript.charset = 'UTF-8';
		sdkStubScript.src = `https://cdn-ukwest.onetrust.com/scripttemplates/otSDKStub.js`;
		sdkStubScript.setAttribute('data-document-language', 'true');
		sdkStubScript.setAttribute('data-domain-script', oneTrustId);
		document.head.appendChild(sdkStubScript);
		

		// Define OptanonWrapper to dispatch consent change event
		(window as any).OptanonWrapper = () => {
			this.ngZone.run(() => {
				window.dispatchEvent(new Event('OnetrustConsentChanged'));
			});
		};
		
		this.isOneTrustLoaded = true;

		// Add consent change listener after scripts load
		sdkStubScript.onload = () => {
			window.addEventListener('OnetrustConsentChanged', this.consentListener);
			this.checkConsent(); // Initial check
		};
	}

	// Helper method to check if current domain needs consent monitoring
	isOUPDomain(): boolean {
		return location.hostname.includes('e-activity.oupchina.com.hk') 
			// || location.hostname.includes('localhost');
	}

	getConsent(): Observable<boolean> {
		// For non-OUP domains, return a static true observable
		if (!this.isOUPDomain()) {
			return new BehaviorSubject<boolean>(true).asObservable();
		}
		// For OUP domains, return the real consent observable
		return this.consentSubject.asObservable();
	}

	get currentConsent(): boolean {
		if (!this.isOUPDomain()) {
			return true; // Always true for non-OUP
		}
        return this.consentSubject.value;
    }

	private checkConsent() {
		this.ngZone.run(() => {
			const activeGroups = window["OnetrustActiveGroups"] || '';
			let hasConsent = activeGroups.includes('C0004');
			this.consentSubject.next(hasConsent); // Targeting Cookies
		});
	}

	// Check if scripts should be loaded for the account
	initializeForAccount(): void {
		let oneTrustId = environment.oupCookies.otId;
		let gtmId = environment.oupCookies.gtmId;

		this.loadOneTrust(oneTrustId);
		this.loadGtm(gtmId);
	}

	changeLang(langCode: string) {
		if (typeof window !== 'undefined' && window['OneTrust']) {
			const lang  = langCode === 'en' ? "en" : "zh-hant";
			window['OneTrust'].changeLanguage(lang);
		}
	}

	openCookiesInfo() {
		if (typeof window !== 'undefined' && window['OneTrust']) {
			const oneTrust = window['OneTrust'];
			if (oneTrust && typeof oneTrust.ToggleInfoDisplay === 'function') {
				oneTrust.ToggleInfoDisplay();
			} else {
				console.warn('OneTrust is not available or ToggleInfoDisplay method is not supported. Ensure OneTrust SDK is loaded.');
			}
		}
	}

	requestConsent() {
		const msg = this.translate.instant("ro.cookies.requireConsent");
		this.als.alert2(msg, null, { btns: [
			[
				{ 
					key: 'request', 
					labelKey: this.translate.instant('ro.cookies.setting'), 
					ngStyle: { backgroundColor: "var(--alert-okbuttoncolor)" } 
					
				}
			],
			['cancel']
		]}).then((key: string) => {
			if (key == 'request') {
				this.openCookiesInfo();
			}
		})
	}
}